import { useEffect, useState } from 'react';

import clsx from 'clsx';
import { matchSorter } from 'match-sorter';

import useTranslation from '@/utils/hooks/translation/useTranslation';
//hook
import useAcl from '@/utils/hooks/user/useAcl';

import { checkValidDashboardCustomization } from '@/utils/pdf/formatData/checkValidDashboardCustomization';

import PropTypes from 'prop-types';

import { aclFeatures } from '@/lib/acl/aclFeatures';
// Lib
import { BoxlabelOptions } from '@/lib/reports/reportOptions';

import Checkbox from '@/components/base/Checkbox';
import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';
// Components
import { DefaultSearchBox } from '@/components/composite/SearchBox/DefaultSearchBox';
import DashboardItem from '@/components/pages/reports/components/Items/Types/DashboardItem';

function CustomDashboardBadge({ dashboardData }) {
    const { t } = useTranslation('reports');
    const { hasAcl } = useAcl();
    if (
        hasAcl(aclFeatures.report.white_label.read) &&
        checkValidDashboardCustomization(dashboardData?.report_customization)
    ) {
        return (
            <span
                className="inline-flex items-center space-x-1 rounded bg-icoPurple-100 px-1 py-1 text-xs text-icoPurple dark:bg-icoPurple/20 xl:px-2"
                aria-label="custom-dashboard-badge">
                <Icon icon="magic-wand" />
                <span className="hidden xl:block" aria-label="quote">
                    {t('create.custom_report_badge')}
                </span>
            </span>
        );
    }
    return null;
}

const DashboardItemCheckbox = ({ allItems, isSubmitting, checkValue, id, disabled }) => {
    const { t } = useTranslation('commons');
    const { hasAcl } = useAcl();

    if (!allItems?.length) {
        {
            BoxlabelOptions(t)[id].empty;
        }
    }

    return allItems.map((item, key) => {
        const isDashboardCustomization =
            hasAcl(aclFeatures.report.white_label.read) &&
            checkValidDashboardCustomization(item?.data?.report_customization);
        return (
            <div className={clsx('flex items-center justify-between')} key={key}>
                <DashboardItem
                    socialProfiles={item?.data?.social_profiles}
                    spGroups={item?.data?.groups}
                    item={item}
                    className={clsx({
                        'w-[calc(100%-56px)] xl:w-[calc(100%-120px)]': isDashboardCustomization,
                        'w-[calc(100%-36px)]': !isDashboardCustomization,
                        'ml-3.5 pl-20':
                            item?.data?.social_profiles?.length === 0 &&
                            item?.data?.groups?.length === 0
                    })}
                    spClassName="h-11 w-20"
                />

                {item?.disabled ? (
                    <Tooltip
                        content={t('reports:create.empty_dashboard')}
                        placement="top"
                        interactive={true}
                        popperOptions={{
                            //prevent tooltip to pop in the wrong place
                            strategy: 'fixed'
                        }}>
                        <div className="ml-1 flex items-center space-x-2">
                            <CustomDashboardBadge dashboardData={item?.data ?? {}} />
                            <Checkbox
                                id={item?.id}
                                onChange={() => checkValue(item?.id)}
                                checked={item?.selected ?? false}
                                disabled={isSubmitting || disabled || item?.disabled}
                            />
                        </div>
                    </Tooltip>
                ) : (
                    <div className="ml-1 flex items-center space-x-2">
                        <CustomDashboardBadge dashboardData={item?.data ?? {}} />
                        <Checkbox
                            id={item?.id}
                            onChange={() => checkValue(item?.id)}
                            checked={item?.selected ?? false}
                            disabled={isSubmitting || disabled || item?.disabled}
                        />
                    </div>
                )}
            </div>
        );
    });
};

const DashboardItemSearchKey = [{ threshold: matchSorter.rankings.CONTAINS, key: 'name' }];

const SearchBoxDashboard = ({ list, onSetList, errors, isLoading = false, disabled }) => {
    const { t } = useTranslation('');
    const [castData, setCastData] = useState([]);
    useEffect(() => {
        let newList = [];
        newList = list.map((item) => {
            return {
                ...item,
                selected: item.select
            };
        });
        setCastData(newList);
    }, [list]);

    const onSetValues = (values) => {
        const updatedData = castData.map(
            (entry) => values?.find((updatedEntry) => entry?.id === updatedEntry?.id) ?? entry
        );
        setCastData(updatedData);
        onSetList(values.filter((item) => item.selected));
    };

    return (
        <DefaultSearchBox
            onSetValues={onSetValues}
            mutateList={null}
            errorList={null}
            searchKeys={DashboardItemSearchKey}
            id="dashboard"
            list={castData}
            isLoading={isLoading}
            errors={!isLoading ? errors : null}
            disabled={disabled}
            noChoicesMessage={t('reports:general.dashboard_no_items')}>
            {({ isSubmitting, checkValue, choices, id, disabled }) => {
                return (
                    <DashboardItemCheckbox
                        allItems={choices}
                        isSubmitting={isSubmitting}
                        checkValue={checkValue}
                        id={id}
                        disabled={disabled}
                    />
                );
            }}
        </DefaultSearchBox>
    );
};

SearchBoxDashboard.propTypes = {
    onSetList: PropTypes.func.isRequired,
    list: PropTypes.array.isRequired,
    errors: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isLoading: PropTypes.bool,
    disabled: PropTypes.bool
};

export default SearchBoxDashboard;
